import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchData } from "../../helpers/getData/fetchData";

import { LPTable } from "./LPTable";
import { LPTitleData } from "./LPTitleData";
import { LPTitleDataSmall } from "./LPTitleDataSmall";
import { numberFormat } from "../../../helpers/numberFormat";
import LPTableSmallWrapper from "./LPTableSmallWrapper";

export const LPView = ({ id }) => {
  const { t } = useTranslation("global");

  const [fundList, setFundList] = useState("isLoading");
  const [urlList, setUrlList] = useState("isLoading");

  useEffect(() => {
    if (id) {
      fetchData(setFundList, `/investmentsFunds/allMyData?id=${id}`, t);
    } else {
      fetchData(setFundList, `/investmentsFunds/allMyData`, t);
    }
    fetchData(setUrlList, "/fundDistributions/getUrlVideoList", t);
  }, [t, id]);

  const createGeneralSheet = (fund, url) => {
    const total = fund.total;
    const amount = fund.amount;
    const percentagePaid = fund.fund.percentagePaid || 0;
    const percentageDistributed = fund.fund.percentageDistributed || 0;
    const portfolioValue = fund.portfolioValue || 0;
    const urlString = url?.urlVideo ? url?.urlVideo : "";

    const dpi = percentagePaid ? (percentageDistributed / percentagePaid) : 0;
    const rvpi = (total * (percentagePaid / 100)) ? (portfolioValue / (total * (percentagePaid / 100))) : 0;
    const tvpi = dpi + rvpi;

    return {
      sheetName: t("toExcel.generalLPPositions"),
      columns: [
        t("latentIvestments.commitment"),
        t("latentIvestments.fund"),
        `${fund.whoInvest}`,
      ],
      rows: [
        [
          t("latentIvestments.amountCommitted"),
          numberFormat(total),
          numberFormat(amount),
        ],
        [
          t("latentIvestments.participationFund"),
          "100%",
          `${((amount * 100) / total).toFixed(2)}%`,
        ],
        [
          t("latentIvestments.percentage"),
          `${percentagePaid}%`,
          `${percentagePaid}%`,
        ],
        [
          t("latentIvestments.amountOfPaid"),
          numberFormat(total * (percentagePaid / 100)),
          numberFormat(amount * (percentagePaid / 100)),
        ],
        [
          t("latentIvestments.percentageDistributed"),
          `${Number(percentageDistributed).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`,
          `${Number(percentageDistributed).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`,
        ],
        [
          t("latentIvestments.amountDistributed"),
          numberFormat(total * (percentageDistributed / 100)),
          numberFormat(amount * (percentageDistributed / 100)),
        ],
        [
          t("latentIvestments.dpi"),
          dpi.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x",
          dpi.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x",
        ],
        [
          t("latentIvestments.rvpi"),
          rvpi.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x",
          rvpi.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x",
        ],
        [
          t("latentIvestments.tvpi"),
          tvpi.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x",
          tvpi.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " x",
        ],
        [t("latentIvestments.fundURL"), urlString, ""],
      ],
    };
  };

  return (
    <>
      {fundList !== "isLoading" && urlList !== "isLoading" ? (
        <Stack gap={0}>
          {fundList.map((fund) => {
            const generalSheet = createGeneralSheet(
              fund,
              urlList.find((obj) => obj.fundId === fund.fundId)
            );
            return (
              <Accordion key={fund.id}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <LPTitleData
                    fund={fund}
                    url={urlList.find((obj) => obj.fundId === fund.fundId)}
                  />
                  <LPTitleDataSmall
                    fund={fund}
                    url={urlList.find((obj) => obj.fundId === fund.fundId)}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <LPTable
                    fundId={fund.fund.id}
                    percentage={(fund.amount * 100) / fund.total}
                    generalSheet={generalSheet}
                  />
                  <LPTableSmallWrapper
                    fundId={fund.fund.id}
                    percentage={(fund.amount * 100) / fund.total}
                    generalSheet={generalSheet}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      ) : (
        <Stack width="100%" alignItems="center" marginTop="100px">
          <CircularProgress />
        </Stack>
      )}
    </>
  );
};
